import React from 'react';
import { Alert, Box, Button, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { getAudit } from '../api/botOpsService';
import { BotOpsHttpError } from '../api/botOpsClient';
import { AuditRow } from '../lib/types';
import { AUDIT_TYPES, AUDIT_TYPE_LABEL, AuditType, auditLabel, auditLink } from '../lib/activity';
import { fmtIct } from '../lib/ict';
import { FONT, TABLE_HEAD_SX } from '../lib/brand';

const PAGE = 100;

/** Every decision, undo, bulk action, instruction, nudge, sync, download and Chat post: who, when, which item, from → to. */
export default function AuditPage() {
  const [sp, setSp] = useSearchParams();
  const rawType = sp.get('type');
  const type: AuditType = (AUDIT_TYPES as readonly string[]).includes(rawType || '') ? (rawType as AuditType) : 'all';
  const [rows, setRows] = React.useState<AuditRow[]>([]);
  const [more, setMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<BotOpsHttpError | null>(null);

  const load = React.useCallback(async (reset: boolean, before?: string) => {
    setLoading(true);
    try {
      const r = await getAudit({ type: type === 'all' ? null : type, before: reset ? null : before, limit: PAGE });
      const got = r.data || [];
      setRows((cur) => (reset ? got : [...cur, ...got]));
      setMore(got.length === PAGE);
      setError(null);
    } catch (e) {
      setError(e instanceof BotOpsHttpError ? e : new BotOpsHttpError((e as Error).message, 0));
    } finally {
      setLoading(false);
    }
  }, [type]);

  React.useEffect(() => { load(true); }, [load]);

  const exportJson = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `grokbot-audit-${type}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 700, color: 'primary.main', mb: 0.4 }}>Audit trail</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Every change made through Grokbot, newest first. The trail is append-only: rows cannot be edited or deleted. Times are ICT.</Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={type}
          aria-label="Audit type"
          sx={{ maxWidth: '100%', overflowX: 'auto' }}
          onChange={(_e, v: AuditType | null) => { if (v) setSp((p) => { const n = new URLSearchParams(p); if (v === 'all') n.delete('type'); else n.set('type', v); return n; }, { replace: true }); }}
        >
          {AUDIT_TYPES.map((t) => <ToggleButton key={t} value={t} sx={{ textTransform: 'none' }}>{AUDIT_TYPE_LABEL[t]}</ToggleButton>)}
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={exportJson} disabled={rows.length === 0}>Export JSON ({rows.length})</Button>
      </Box>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => load(true)}>Retry</Button>}>
          Could not load the audit trail: {error.message}{rows.length ? ' Showing what was loaded before.' : ''}
        </Alert>
      )}

      {rows.length === 0 && !loading && !error ? (
        <Typography variant="body2" color="text.secondary" data-testid="audit-empty">Nothing has been recorded{type === 'all' ? '' : ` for ${AUDIT_TYPE_LABEL[type].toLowerCase()}`} yet.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Audit trail" sx={{ minWidth: 760 }}>
            <TableHead sx={TABLE_HEAD_SX}>
              <TableRow><TableCell>When (ICT)</TableCell><TableCell>Who</TableCell><TableCell>What</TableCell><TableCell>Item</TableCell><TableCell>From → to</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const link = auditLink(r);
                return (
                  <TableRow key={r.id} data-testid="audit-row">
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtIct(r.at)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.admin_email || 'system'}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{auditLabel(r.action)}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: FONT.mono, maxWidth: 260, wordBreak: 'break-all' }}>
                      {link ? <RouterLink to={link}>{r.item_id}</RouterLink> : (r.item_id || '—')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.from_value || r.to_value ? `${r.from_value || '—'} → ${r.to_value || '—'}` : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      {more && <Button sx={{ mt: 1.5 }} variant="outlined" disabled={loading} onClick={() => load(false, rows[rows.length - 1].id)}>{loading ? 'Loading…' : 'Load more'}</Button>}
      {loading && rows.length === 0 && <Typography variant="body2" color="text.secondary" role="status">Loading…</Typography>}
    </Box>
  );
}
